import React, { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import HeaderNews from '../components/headernews';

const News = () => {
  const [articles, setArticles] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState('Coastal');
  const apiBase = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');

  const decodeEncodedHtml = (value) => {
    const text = value || '';
    if (!text.includes('&lt;') && !text.includes('&gt;') && !text.includes('&amp;')) {
      return text;
    }

    let decoded = text;
    const textarea = document.createElement('textarea');

    // Some DB rows contain nested encoding like &amp;lt;span...&amp;gt;.
    for (let i = 0; i < 3; i += 1) {
      textarea.innerHTML = decoded;
      const next = textarea.value;
      if (next === decoded) {
        break;
      }
      decoded = next;
    }

    return decoded;
  };

  const getSanitizedDescription = (html) => DOMPurify.sanitize(decodeEncodedHtml(html), {
    USE_PROFILES: { html: true },
    FORCE_BODY: true,
    ADD_ATTR: ['style', 'color', 'face', 'size'],
  });

  useEffect(() => {
    if (!apiBase) {
      console.error('REACT_APP_API_BASE is not configured. Skipping news API fetch.');
      setArticles([]);
      return;
    }

    if (selectedMenu === 'Coastal') {
      // Fetch Coastal news from backend
      fetch(`${apiBase}/api/news?section=Coastal`)
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data)) {
            const mapped = data.map(item => ({
              title: item.title,
              description: item.aboutdescription || item.aboutdescription || '',
              url: item.sharelink || null,
              img: item.imagepath || (item.photos && item.photos[0]) || null
            }));
            setArticles(mapped);
          } else {
            setArticles([]);
          }
        })
        .catch(err => {
          console.error('Error fetching Coastal news:', err);
          setArticles([]);
        });
    } else if (selectedMenu === 'Gulf') {
      fetch(`${apiBase}/api/news?section=Gulf`)
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data)) {
            const mapped = data.map(item => ({
              title: item.title,
              description: item.aboutdescription || item.aboutdescription || '',
              url: item.sharelink || null,
              img: item.imagepath || (item.photos && item.photos[0]) || null
            }));
            setArticles(mapped);
          } else {
            setArticles([]);
          }
        })
        .catch(err => {
          console.error('Error fetching Gulf news:', err);
          setArticles([]);
        });
    } else if (selectedMenu === 'Rest of the World') {
      fetch(`${apiBase}/api/news?section=World`)
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data)) {
            const mapped = data.map(item => ({
              title: item.title,
              description: item.aboutdescription || item.aboutdescription || '',
              img: item.imagepath || (item.photos && item.photos[0]) || null
            }));
            setArticles(mapped);
          } else {
            setArticles([]);
          }
        })
        .catch(err => {
          console.error('Error fetching World news:', err);
          setArticles([]);
        });
    } 
  }, [selectedMenu, apiBase]);

  return (
    <div>
      <style>{`
        @media (max-width: 600px) {
          .news-img-center {
            height: 52px !important;
            width: 70px !important;
            margin-right: 10px !important;
          }
        }
      `}</style>

      <HeaderNews />

      <h2 style={{ position: 'absolute', left: '-9999px' }}>News Sections</h2>

      {/* Menu Buttons */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
          {['Coastal', 'Gulf', 'Rest of the World'].map((menu) => (
            <button
              key={menu}
              onClick={() => setSelectedMenu(menu)}
              style={{
                padding: '0.5rem 1.2rem',
                borderRadius: 8,
                border: selectedMenu === menu ? '2.5px solid #1565c0' : '1px solid #ccc',
                background: selectedMenu === menu ? '#e3f2fd' : '#fff',
                color: selectedMenu === menu ? '#0d47a1' : '#333',
                fontWeight: selectedMenu === menu ? 700 : 400,
                fontSize:
                  menu === 'Rest of the World'
                    ? (selectedMenu === menu ? '0.92rem' : '0.85rem')
                    : (selectedMenu === menu ? '1.01rem' : '0.92rem'),
                fontFamily: 'Segoe UI, Arial, sans-serif',
                cursor: 'pointer',
                outline: 'none',
                transition: 'all 0.2s',
              }}
            >
              {menu}
            </button>
          ))}
        </div>
      </div>

      {articles.length === 0 && <p>Loading news...</p>}

      {/* ✅ Added left spacing for news list */}
      <div style={{ paddingLeft: '10px' }}>
        <div style={{ height: 12 }} />

        {/* News List */}
        {articles.map((article, index) => (
          <a
            key={index}
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              marginBottom: 12,
              borderBottom: '1px solid #ccc',
              paddingBottom: 10,
              textDecoration: 'none',
              color: 'inherit',
              borderRadius: 6,
              transition: 'background 0.15s',
              paddingRight: '8px',
            }}
            onMouseOver={(e) => (e.currentTarget.style.background = '#f5f7fa')}
            onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            {/* Image / Placeholder */}
            <div
              className="news-img-center"
              style={{
                width: 70,
                height: 52,
                marginRight: 10,
                flexShrink: 0,
                borderRadius: 6,
                backgroundColor: article.img ? 'transparent' : '#e0e0e0',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              <img
                src={article.img || '/defaultimages/imagenotavailable.jpg'}
                alt={article.title}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: 6,
                  display: 'block',
                }}
              />
            </div>

            {/* Text Section */}
            <div style={{ flex: 1 }}>
              <h3
                style={{
                  fontSize: '1.02rem',
                  fontWeight: 'bold',
                  margin: 0,
                  marginBottom: 4,
                  color: '#1976d2',
                  lineHeight: 1.2,
                }}
              >
                {article.title}
              </h3>
              <div
                style={{ fontSize: '0.93rem', color: '#333', lineHeight: 1.4, whiteSpace: 'pre-line' }}
                dangerouslySetInnerHTML={{ __html: getSanitizedDescription(article.description) }}
              />
            </div>
          </a>
        ))}

        <div style={{ height: 12 }} />
      </div>
    </div>
  );
};

export default News;
